import { useTheme } from "../../theme/useTheme"
import { Typography, alpha, useTheme as muiUseTheme, type SxProps, type Theme } from "@mui/material"
import React from "react"

export const BreakableText = ({ children }: { children: React.ReactNode }) => {
    const processText = (text: string) => {
        return text.replace(/([a-zA-Z])([a-zA-Z])/g, "$1\u00AD$2")
    }

    const processChildren = (child: React.ReactNode): React.ReactNode => {
        if (typeof child === "string") {
            return processText(child)
        } else if (React.isValidElement(child)) {
            const childProps = child.props as { children?: React.ReactNode }
            return React.cloneElement(child, undefined, processChildren(childProps.children))
        } else if (Array.isArray(child)) {
            return child.map((c, i) => <React.Fragment key={i}>{processChildren(c)}</React.Fragment>)
        }
        return child
    }

    const processedChildren = processChildren(children)

    return <>{processedChildren}</>
}

export const Skill = ({ children }: { children: React.ReactNode }) => {
    const { skills, themeSettings, theme } = useTheme()

    if (skills.style === "highlighted") {
        return (
            <Typography
                component="code"
                sx={{
                    fontFamily: '"DM Mono", monospace',
                    backgroundColor: alpha(
                        theme.palette.action.selected,
                        themeSettings.isDarkMode ? 0.08 : 0.05
                    ),
                    color: skills.textColor,
                    // chip recipe from the portfolio's .prose code
                    border: "1px solid",
                    borderColor: themeSettings.borderColor,
                    padding: "0 4px",
                    borderRadius: "4px",
                    fontSize: "0.82em",
                }}
            >
                {children}
            </Typography>
        )
    }

    return <>{children}</>
}

export const Emphasize = ({ children }: { children: React.ReactNode }) => {
    const theme = muiUseTheme()
    return (
        // emphasis carried by weight, not color (a gray shift was too subtle;
        // stays monochrome so grayscale printing survives)
        <Typography
            component="span"
            sx={{ color: alpha(theme.palette.text.primary, 1), fontWeight: 450 }}
        >
            {children}
        </Typography>
    )
}

export const SectionItemTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="subtitle1"
        component="h3"
        sx={{ display: "inline", fontWeight: 600, lineHeight: 1.5 }}
    >
        {children}
    </Typography>
)

export const Deemphasize = ({ children, sx }: { children: React.ReactNode; sx?: SxProps<Theme> }) => {
    const { themeSettings } = useTheme()
    // separators are ornaments: smaller and half-faded — a full-size solid
    // dot in a dense line reads as content, not punctuation
    return (
        <Typography
            component="span"
            sx={{
                fontSize: "0.8em",
                verticalAlign: "0.15em",
                color: alpha(themeSettings.faintColor, 0.6),
                ...sx,
            }}
        >
            {children}
        </Typography>
    )
}
